const alphaMin = 0.001;
const alphaDecay = 1 - Math.pow(alphaMin, 1 / 300);
const alphaTarget = 0;
const velocityDecay = 0.6;
const initialRadius = 10;
const initialAngle = Math.PI * (3 - Math.sqrt(5));

function clamp(x, a, b) {
  return Math.max(a, Math.min(x, b));
}

function jiggle() {
  return (Math.random() - 0.5) * 1e-6;
}

function splitLabel(label) {
  if (label.length <= 8) return [label];
  let best = -1, bestDist = Infinity;
  for (let i = 0; i < label.length; i++) {
    if (label[i] === " ") {
      const dist = Math.abs(i - label.length / 2);
      if (dist < bestDist) { bestDist = dist; best = i; }
    }
  }
  if (best === -1) return [label];
  return [label.slice(0, best), label.slice(best + 1)];
}

function nodeColor(node) {
  if (node.type === "client") return "#2D825F";
  if (node.type === "freelance") return "#ABBFF4";
  return "#D34A00";
}

function parseGraph({ nodes, links }) {
  for (let i = 0, n = links.length, link; i < n; ++i) {
    link = links[i], link.index = i;
    link.src = nodes[link.src];
    link.dst = nodes[link.dst];
  }
  for (let i = 0, n = nodes.length, node; i < n; ++i) {
    node = nodes[i], node.index = i;
    if (node.fx != null) node.x = node.fx;
    if (node.fy != null) node.y = node.fy;
    if (isNaN(node.x) || isNaN(node.y)) {
      let radius = initialRadius * Math.sqrt(0.5 + i), angle = i * initialAngle;
      node.x = radius * Math.cos(angle);
      node.y = radius * Math.sin(angle);
    }
    if (isNaN(node.vx) || isNaN(node.vy)) {
      node.vx = node.vy = 0;
    }
  }
  const parents = new Set();
  for (const link of links) {
    parents.add(link.src.index);
  }
  for (const node of nodes) {
    node.isRoot = parents.has(node.index) && node.index === 0;
    node.isParent = parents.has(node.index) && !node.isRoot;
    node.lines = splitLabel(node.name);
  }
  for (const link of links) {
    if (link.src.type && !link.dst.type) {
      link.dst.type = link.src.type;
    }
    link.dst.parent = link.src;
  }
  return { nodes, links };
}

function createSimulation({ nodes, links }) {
  let alpha = 1;

  return {
    get alpha() { return alpha; },
    reheat() { alpha = Math.max(alpha, 0.3); },
    nodeAt(x, y) {
      // Search in reverse so topmost drawn node is picked first
      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i];
        const dx = Math.abs(x - node.x);
        const dy = Math.abs(y - node.y);
        if (node.hw) {
          if (dx < node.hw && dy < node.hh) return node;
        } else {
          if (dx * dx + dy * dy < 100) return node;
        }
      }
      return null;
    },
    tick({ width, height }) {
      alpha += (alphaTarget - alpha) * alphaDecay;

      // Many-body force
      const strength = -40;
      const distanceMin2 = 1;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          if (dx === 0) dx = jiggle();
          if (dy === 0) dy = jiggle();
          let d = dx * dx + dy * dy;
          if (d < distanceMin2) d = Math.sqrt(distanceMin2 * d);
          const w = strength * alpha / d;
          const fx = dx * w;
          const fy = dy * w;
          a.vx += fx;
          a.vy += fy;
          b.vx -= fx;
          b.vy -= fy;
        }
      }

      // Link force
      const linkStrength = 0.1;
      for (const l of links) {
        const linkDistance = l.dst.isParent ? 40 : 20;
        let dx = l.dst.x - l.src.x;
        let dy = l.dst.y - l.src.y;
        let d = Math.sqrt(dx * dx + dy * dy) || 1;
        const w = (d - linkDistance) / d * linkStrength * alpha;
        const fx = dx * w;
        const fy = dy * w;
        l.src.vx += fx;
        l.src.vy += fy;
        l.dst.vx -= fx;
        l.dst.vy -= fy;
      }

      // Collision force
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          if (dx === 0) dx = jiggle();
          if (dy === 0) dy = jiggle();
          let l = Math.sqrt(dx * dx + dy * dy) || 1;
          const minDist = (a.isParent || b.isParent) ? 54 : 16;
          if (l < minDist) {
            const w = (l - minDist) / l * 0.5;
            const fx = dx * w;
            const fy = dy * w;
            a.vx += fx;
            a.vy += fy;
            b.vx -= fx;
            b.vy -= fy;
          }
        }
      }

      // Centering force
      let cx = 0, cy = 0;
      for (const n of nodes) cx += n.x, cy += n.y;
      cx /= nodes.length; cy /= nodes.length;
      for (const n of nodes) n.x -= cx - width / 2, n.y -= cy - height / 2;

      for (let i = 0; i < nodes.length; i++) {
        let n = nodes[i];
        if (n.fx != null) n.x = n.fx, n.vx = 0;
        else n.x += n.vx *= velocityDecay;
        if (n.fy != null) n.y = n.fy, n.vy = 0;
        else n.y += n.vy *= velocityDecay;
      }
    }
  }
}

function createRenderer({ c }) {
  return {
    draw({ nodes, links, width, height }) {
      c.clearRect(0, 0, width, height);

      c.save();
      c.strokeStyle = "#000";
      c.beginPath();
      for (const link of links) {
        c.moveTo(link.src.x, link.src.y);
        c.lineTo(link.dst.x, link.dst.y);
      }
      c.stroke();
      c.restore();

      c.save();
      for (const node of nodes) {
        if (node.isRoot || node.isParent) {
          const label = node.name;

          c.textBaseline = "middle";
          c.font = node.isRoot ? "740 12px/1 ABCWalterNeue" : "400 12px/1 InputMono";
          c.textAlign = "center";

          const px = 6;
          const py = 4;
          const m = c.measureText(label);
          const w = m.width + 2 * px;
          const h = m.fontBoundingBoxDescent + m.fontBoundingBoxAscent + 2 * py;

          node.hw = 0.5 * w;
          node.hh = 0.5 * h;

          c.fillStyle = "#fff";
          c.strokeStyle = "#000";

          c.beginPath();
          c.roundRect(node.x - 0.5 * w, node.y - 0.5 * h - 1, w, h, 4);
          c.fill();
          c.stroke();

          c.fillStyle = "#000";

          c.fillText(label, node.x, node.y);
        } else {
          c.strokeStyle = "#000";
          c.fillStyle = nodeColor(node);

          c.lineWidth = node.hover ? 2 : 1;
          c.beginPath();
          c.arc(node.x, node.y, 5, 0, 2 * Math.PI);
          c.fill();
          c.stroke();
          c.lineWidth = 1;

          c.fillStyle = "#000";
          c.textBaseline = "middle";
          c.font = "400 12px/1 InputMono";

          const lx = node.parent.x - node.x;
          const ly = node.parent.y - node.y;
          const ll = Math.sqrt(lx * lx + ly * ly) || 1;
          const ux = -lx / ll;
          const uy = -ly / ll;

          const lines = node.lines;
          const lineHeight = 14;

          let lineMaxWidth = 0;
          for (const line of lines)
            lineMaxWidth = Math.max(lineMaxWidth, c.measureText(line).width);
          const textHeight = lines.length * lineHeight;

          const hw = lineMaxWidth / 2, hh = textHeight / 2;
          const gap = 8;
          const aux = Math.abs(ux), auy = Math.abs(uy);
          let d;
          if (auy * (gap + hw) <= aux * hh) {
            d = (gap + hw) / aux;
          } else if (aux * (gap + hh) <= auy * hw) {
            d = (gap + hh) / auy;
          } else {
            const s = aux * hw + auy * hh;
            d = s + Math.sqrt(s * s - hw * hw - hh * hh + gap * gap);
          }
          const x = node.x + ux * d;
          const y = node.y + uy * d;
          const y0 = y - (lines.length - 1) * lineHeight / 2;

          c.textAlign = "center";
          for (let i = 0; i < lines.length; i++) {
            c.fillText(lines[i], x, y0 + i * lineHeight);
          }
        }
      }
      c.restore();
    }
  }
}

class CreditsGraphElement extends HTMLElement {
  #canvas = null;
  #canvasWidth = 0;
  #canvasHeight = 0;
  #canvasRect = null;
  #observer = null;
  #simulation = null;
  #renderer = null;
  #nodes = null;
  #links = null;
  #raf = null;
  #dirty = false;
  #dragNode = null;
  #hoverNode = null;
  #downX = 0;
  #downY = 0;

  connectedCallback() {
    const dpi = devicePixelRatio;
    this.#canvasWidth = 436;
    this.#canvasHeight = 436;

    let canvas = this.#canvas = (this.#canvas || this.appendChild(document.createElement("canvas")));
    canvas.width = dpi * this.#canvasWidth;
    canvas.height = dpi * this.#canvasHeight;
    canvas.style.width = `${this.#canvasWidth}px`;

    let c = canvas.getContext("2d");
    c.scale(dpi, dpi);

    canvas.addEventListener("pointerdown", this);
    canvas.addEventListener("pointermove", this);
    canvas.addEventListener("pointerleave", this);

    const data = JSON.parse(this.querySelector("script").textContent);
    const { nodes, links } = parseGraph(data);
    this.#nodes = nodes;
    this.#links = links;
    this.#simulation = createSimulation({ nodes, links });
    this.#renderer = createRenderer({ c });
    this.#scheduleFrame();

    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.#simulation.reheat();
        this.#scheduleFrame();
      }
    });
    this.#observer.observe(canvas);
  }

  #frame = () => {
    this.#raf = null;
    if (this.#simulation.alpha >= alphaMin || this.#dragNode) {
      this.#simulation.tick({
        width: this.#canvasWidth,
        height: this.#canvasHeight,
      });
    }
    this.#renderer.draw({
      width: this.#canvasWidth,
      height: this.#canvasHeight,
      nodes: this.#nodes,
      links: this.#links,
    });
    if (this.#simulation.alpha >= alphaMin || this.#dragNode || this.#dirty) {
      this.#dirty = false;
      this.#raf = requestAnimationFrame(this.#frame);
    }
  };

  #scheduleFrame() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(this.#frame);
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    cancelAnimationFrame(this.#raf);
    removeEventListener("pointermove", this);
    removeEventListener("pointerup", this);
  }

  handleEvent(e) {
    if (e.type === "pointerdown") {
      const r = this.#canvasRect = this.#canvas.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      const node = this.#simulation.nodeAt(x, y);
      if (!node) return;
      this.#dragNode = node;
      this.#downX = e.clientX;
      this.#downY = e.clientY;
      this.#dragNode.fx = clamp(x, 0, this.#canvasWidth);
      this.#dragNode.fy = clamp(y, 0, this.#canvasHeight);
      this.#simulation.reheat();
      this.#scheduleFrame();
      addEventListener("pointermove", this);
      addEventListener("pointerup", this);
    } else if (e.type === "pointermove") {
      if (e.currentTarget === this.#canvas) {
        if (this.#dragNode) return;
        const r = this.#canvas.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        const node = this.#simulation.nodeAt(x, y);
        if (node !== this.#hoverNode) {
          if (this.#hoverNode) this.#hoverNode.hover = false;
          this.#hoverNode = node;
          if (node) node.hover = true;
          this.#canvas.style.cursor = node?.url ? "pointer" : "";
          this.#dirty = true;
          this.#scheduleFrame();
        }
      } else if (this.#dragNode) {
        const x = e.clientX - this.#canvasRect.left, y = e.clientY - this.#canvasRect.top;
        this.#dragNode.fx = clamp(x, 0, this.#canvasWidth);
        this.#dragNode.fy = clamp(y, 0, this.#canvasHeight);
        this.#simulation.reheat();
        this.#scheduleFrame();
      }
    } else if (e.type === "pointerleave") {
      if (this.#hoverNode) {
        this.#hoverNode.hover = false;
        this.#hoverNode = null;
        this.#canvas.style.cursor = "";
        this.#dirty = true;
        this.#scheduleFrame();
      }
    } else if (e.type === "pointerup") {
      removeEventListener("pointermove", this);
      removeEventListener("pointerup", this);
      if (!this.#dragNode) return;
      const dx = e.clientX - this.#downX, dy = e.clientY - this.#downY;
      const wasClick = dx * dx + dy * dy < 9;
      if (wasClick && this.#dragNode.url) {
        window.open(this.#dragNode.url, "_blank", "noreferrer");
      }
      delete this.#dragNode.fx;
      delete this.#dragNode.fy;
      this.#dragNode = null;
    }
  }
}

customElements.define("credits-graph", CreditsGraphElement);
